import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ShieldAlert, CheckCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/Toast';
import { supabaseClient } from '../lib/supabase';
export function ForcePasswordChangeModal() {
  const {
    user,
    mustChangePassword,
    clearTempPassword
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!mustChangePassword || !user) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showToast('warning', 'Contraseña muy corta', 'Debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', 'No coinciden', 'Las contraseñas no son iguales');
      return;
    }
    setSaving(true);
    try {
      // Update password and remove tempPassword flag
      await supabaseClient.updateEmployee(user.employeeId || user.id, {
        password: newPassword,
        permissions: {
          ...user.permissions,
          tempPassword: false
        }
      });
      clearTempPassword();
      showToast('success', '¡Contraseña actualizada!', 'Ya puedes usar el sistema normalmente');
    } catch (error) {
      console.error('Error updating password:', error);
      showToast('error', 'Error', 'No se pudo actualizar la contraseña. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };
  const isValid = newPassword.length >= 6 && newPassword === confirmPassword;
  return <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4" data-id="element-397">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-398">
        {/* Header accent */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-500" data-id="element-399" />

        <div className="p-8" data-id="element-400">
          {/* Icon */}
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-401">
            <ShieldAlert size={32} className="text-amber-600 dark:text-amber-400" data-id="element-402" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2" data-id="element-403">
            Cambiar Contraseña
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-8 max-w-xs mx-auto" data-id="element-404">
            Tu cuenta tiene una contraseña temporal. Por seguridad, debes crear
            una nueva contraseña para continuar.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-405">
            {/* New Password */}
            <div data-id="element-406">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-407">
                Nueva Contraseña
              </label>
              <div className="relative" data-id="element-408">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-409" />
                <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Mínimo 6 caracteres" autoFocus required minLength={6} data-id="element-410" />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-411">
                  {showNew ? <EyeOff size={18} data-id="element-412" /> : <Eye size={18} data-id="element-413" />}
                </button>
              </div>
              {newPassword && newPassword.length < 6 && <p className="text-xs text-red-500 mt-1" data-id="element-414">
                  Debe tener al menos 6 caracteres
                </p>}
              {newPassword.length >= 6 && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1" data-id="element-415">
                  <CheckCircle size={12} data-id="element-416" /> Longitud válida
                </p>}
            </div>

            {/* Confirm Password */}
            <div data-id="element-417">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-418">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-419">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-420" />
                <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Repite la contraseña" required data-id="element-421" />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-422">
                  {showConfirm ? <EyeOff size={18} data-id="element-423" /> : <Eye size={18} data-id="element-424" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== newPassword && <p className="text-xs text-red-500 mt-1" data-id="element-425">
                  Las contraseñas no coinciden
                </p>}
              {confirmPassword && confirmPassword === newPassword && newPassword.length >= 6 && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1" data-id="element-426">
                    <CheckCircle size={12} data-id="element-427" /> Las contraseñas coinciden
                  </p>}
            </div>

            <Button type="submit" fullWidth disabled={!isValid || saving} className="h-12 text-base font-bold mt-2" data-id="element-428">
              {saving ? 'Guardando...' : 'Establecer Nueva Contraseña'}
            </Button>
          </form>
        </div>
      </div>
    </div>;
}